import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { useForm } from 'react-hook-form';
import { getUserRegistrationStyles } from './userRegistrationStyles';
import { ArrowRight2 } from 'iconsax-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useEventRegistrationTranslation } from '@ui/layout/EventRegistration/hooks/useEventRegistrationTranslation';
import { ControlledFilledTextField } from '@ui/components/common/TextField';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import DOMPurify from 'dompurify';
import { validateEventPasswordAsync } from '@application/features/vdkEvent/VDKEventThunk';
import { VDKEventSlice } from '@application/features/vdkEvent/VDKEventSlice';
import { IconButton, useTheme } from '@mui/material';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import LocalStorageService from '@application/common/local-storage-service';
import { isEmptyHtml } from '@application/helpers/html-parser';
import { getRegistrationDateLocale, formatRegistrationDate, formatRegistrationTime } from '@application/utilities/registration-date-time';
import { replaceWelcomePlaceholders, hasWelcomePlaceholders } from '@application/utilities/welcome-text-placeholders';
import { useEventRegistrationLanguageContext } from '@ui/layout/EventRegistration/EventRegistrationLanguageContext';
const EventRegistrationStart = ({ handleStartRegistration, previewLayout }) => {
    const styles = useMemo(() => getUserRegistrationStyles(previewLayout), [previewLayout]);
    const theme = useTheme();
    const { t, activeLanguageCode } = useEventRegistrationTranslation();
    const navigate = useNavigate();
    const { e } = useParams();
    const dispatch = useAppDispatch();
    const { VDKEventForRegistration } = useAppSelector((state) => state.VDKEvent);
    const [showPasswordField, setShowPasswordField] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const { eventLanguages, selectedLanguageId } = useEventRegistrationLanguageContext();
    const hasWelcome = useMemo(() => !isEmptyHtml(VDKEventForRegistration?.welcomeText), [VDKEventForRegistration?.welcomeText]);
    const dateLocale = useMemo(() => getRegistrationDateLocale(eventLanguages ?? [], selectedLanguageId, activeLanguageCode), [eventLanguages, selectedLanguageId, activeLanguageCode]);
    const displayDate = useMemo(() => formatRegistrationDate(VDKEventForRegistration ?? {}, dateLocale), [VDKEventForRegistration, dateLocale]);
    const displayTime = useMemo(() => formatRegistrationTime(VDKEventForRegistration ?? {}, dateLocale), [VDKEventForRegistration, dateLocale]);
    const welcomeContainsPlaceholders = useMemo(() => hasWelcomePlaceholders(VDKEventForRegistration?.welcomeText), [VDKEventForRegistration?.welcomeText]);
    const resolvedWelcomeHtml = useMemo(() => {
        const raw = VDKEventForRegistration?.welcomeText;
        if (!raw)
            return '';
        const replaced = replaceWelcomePlaceholders(raw, {
            dateRange: displayDate,
            timeRange: displayTime,
            location: VDKEventForRegistration?.formattedLocation ?? ''
        });
        const normalized = replaced.replace(/\n/g, ' ');
        return DOMPurify.sanitize(normalized, {
            ALLOWED_ATTR: ['style', 'class', 'href', 'src', 'alt'],
            ALLOWED_TAGS: [
                'a',
                'b',
                'blockquote',
                'br',
                'caption',
                'code',
                'div',
                'em',
                'h1',
                'h2',
                'h3',
                'h4',
                'h5',
                'h6',
                'hr',
                'i',
                'img',
                'li',
                'ol',
                'p',
                'pre',
                'small',
                'span',
                'strike',
                'strong',
                'sub',
                'sup',
                'table',
                'tbody',
                'td',
                'tfoot',
                'th',
                'thead',
                'tr',
                'u',
                'ul'
            ],
            FORBID_ATTR: [],
            ADD_ATTR: ['style', 'class', 'target'],
            ADD_TAGS: ['iframe'],
            KEEP_CONTENT: true
        });
    }, [
        VDKEventForRegistration?.welcomeText,
        VDKEventForRegistration?.formattedLocation,
        displayDate,
        displayTime
    ]);
    const handleTogglePasswordVisibility = () => {
        setShowPassword((prev) => !prev);
    };
    useEffect(() => {
        dispatch(VDKEventSlice.actions.resetEventValidation());
        LocalStorageService.removeEventToken();
    }, [dispatch, e]);
    useEffect(() => {
        setShowPasswordField(Boolean(VDKEventForRegistration?.isPasswordProtection));
    }, [VDKEventForRegistration?.isPasswordProtection]);
    const { control, handleSubmit, formState: { errors } } = useForm({ defaultValues: { email: '', password: '' } });
    const onSubmit = async (data) => {
        // Save the selected language for this event before navigating
        if (e && selectedLanguageId) {
            LocalStorageService.setEventLanguage(e, selectedLanguageId);
        }
        // If password protection is enabled, validate the password
        if (showPasswordField && data.password) {
            try {
                const result = await dispatch(validateEventPasswordAsync({
                    eventId: e,
                    password: data.password
                })).unwrap();
                if (result.isValid) {
                    const eventToken = `${e}_${Date.now()}`;
                    LocalStorageService.setEventToken(eventToken);
                    handleStartRegistration
                        ? handleStartRegistration()
                        : navigate(`/event-registration/${e}/form?language=${selectedLanguageId}`);
                }
                else {
                    toast.error(t('eventRegistration.login.invalidPassword'));
                }
            }
            catch (error) {
                console.error('Password validation error:', error);
                toast.error(t('eventRegistration.login.passwordValidationFailed'));
            }
        }
        else {
            const eventToken = `${e}_${Date.now()}`;
            LocalStorageService.setEventToken(eventToken);
            handleStartRegistration
                ? handleStartRegistration()
                : navigate(`/event-registration/${e}/form?language=${selectedLanguageId}`);
        }
    };
    return (_jsx(Box, { sx: styles.root, children: _jsx(Box, { sx: styles.card, children: _jsxs(Box, { sx: styles.content, children: [hasWelcome ? (_jsx(_Fragment, { children: _jsx(Box, { className: 'ql-editor', sx: styles.welcomeMessage, dangerouslySetInnerHTML: { __html: resolvedWelcomeHtml } }) })) : (_jsxs(_Fragment, { children: [_jsx(Typography, { sx: styles.welcomeLabel, children: t('eventRegistration.login.welcomeTo') }), _jsx(Typography, { sx: styles.welcomeTitle, children: VDKEventForRegistration?.title }), displayDate && (_jsx(Typography, { sx: styles.welcomeDate, children: t('eventRegistration.login.happeningOn', {
                                    defaultValue: 'Happening on {{date}}',
                                    date: displayDate
                                }) })), _jsx(Typography, { variant: 'body2', color: '#637381', children: t('eventRegistration.login.companyBy', { companyName: t('common.vdkGroup') }) })] })), showPasswordField && (_jsxs(Box, { children: [_jsx(Typography, { variant: 'body2', mt: 0, mb: 1, color: 'text.primary', children: t('eventRegistration.login.enterPassword') }), _jsx(ControlledFilledTextField, { bg: 'grey8', control: control, name: 'password', type: showPassword ? 'text' : 'password', slotProps: {
                                    input: {
                                        endAdornment: (_jsx(IconButton, { size: 'medium', onClick: handleTogglePasswordVisibility, "aria-label": String(t(showPassword
                                                ? 'eventRegistration.login.hidePassword'
                                                : 'eventRegistration.login.showPassword')), sx: {
                                                border: 'none',
                                                '&:hover': {
                                                    backgroundColor: 'transparent'
                                                }
                                            }, children: _jsx(Icon, { icon: showPassword ? 'solar:eye-bold' : 'solar:eye-closed-bold', color: theme.palette.grey[600] }) }))
                                    }
                                }, label: t('labels.password'), errorMessage: errors.password?.message, rules: {
                                    required: String(t('eventRegistration.form.required', { field: t('labels.password') }))
                                } })] })), _jsx(Button, { type: 'button', fullWidth: true, endIcon: _jsx(ArrowRight2, { size: 22 }), sx: styles.startButton, onClick: handleSubmit(onSubmit), size: 'large', variant: 'contained', disabled: !VDKEventForRegistration ||
                            !VDKEventForRegistration.formSteps ||
                            VDKEventForRegistration.formSteps.length === 0, children: t('eventRegistration.login.startRegistration') })] }) }) }));
};
export default EventRegistrationStart;
